import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Map, Calendar, Users, ArrowRight, ArrowLeft, Sun, TrendingUp, CheckCircle2, ChevronRight, Send, PlusIcon, CompassIcon, Star, Plane, Loader, Wallet, MapPin, Mic, Volume2, Edit2, Cloud, Thermometer, Camera, Coffee, Utensils } from 'lucide-react';
import { useLocalization } from '../hooks/useLocalization';
import { searchTourvisio } from '../services/tourvisioService';
import { sendChatToAI } from '../services/aiService';
import { fetchDestinationImage } from '../services/imageService';
import type { Hotel, ChatMessage, SearchQuery, Offer } from '../types';
import { SearchType } from '../types';
import { HotelDetail } from './HotelDetail';

interface PlannerData {
    assistant_reply?: string;
    suggestion_chips?: { label: string; icon?: string }[];
    interactive_cards?: any[];
    search_params?: any;
    status?: string;
    route_insight?: { vibe: string; trend: string; local_tip: string };
    budget_analysis?: { original_cost: number; user_budget: number; gap: number };
    itinerary?: { day: number; title: string; activity: string }[];
    weather?: { temp: string; condition: string; season: string };
    places_to_visit?: string[];
    activities?: string[];
}

const VISUAL_STEPS = {
    DEPARTURE: 0,
    DESTINATION: 1,
    DATE: 2,
    DURATION: 3,
    TRAVELERS: 4,
    RESULTS: 5
};

// Text Dictionary for Localization
const TEXTS = {
    se: {
        stepDep: "Varifrån reser du?",
        subDep: "Välj din avreseort.",
        stepDest: "Vart vill du resa?",
        subDest: "Välj ditt drömresmål för att börja planera.",
        stepDate: "När vill du åka?",
        subDate: "Välj månad för din resa.",
        stepDur: "Hur länge?",
        subDur: "Välj reslängd.",
        stepPpl: "Vilka åker?",
        subPpl: "Ange antal resenärer.",

        aiInput: "Eller beskriv din drömresa här...",
        aiPlaceholder: "T.ex. 'Lyxhotell i Dubai med barnpool nära stranden...'",
        aiBtn: "Fråga AI & Sök",
        back: "TILLBAKA",

        // Results & Dashboard
        restart: "Starta om",
        loading: "Lumi AI analyserar bästa alternativen...",
        insightHeader: "Lumi Insight",
        budgetHeader: "Budgetanalys",
        yourBudget: "Din Budget",
        status: "Status",
        notSpecified: "Ej Angiven",
        routeHeader: "Route Insight",
        trend: "Trend",
        localTip: "Lokalt Tips",
        recommendations: "Rekommendationer för dig",
        seePrice: "Se pris",
        select: "Välj Detta",
        hotelPkg: "Hotellpaket & Flyg",
        uniqueExp: "Unik Upplevelse",
        noResults: "Inga resultat hittades just nu.",
        tryAgain: "Försök igen",

        // Vision Board
        vibeCheck: "Stämnings­koll",
        eternalSummer: "Evig Sommar",
        forecast: "Prognos",
        investment: "Investering",
        highlights: "Höjdpunkter",
        visualTimeline: "Den Visuella Tidslinjen",
        dayLabel: "Dag",
        discoverMore: "Upptäck mer",
        expertPicks: "Utvalda Tips",
        aiInsightLabel: "AI Insikt",
        vibe: "Känsla",
        recommendationsLabel: "Rekommendationer",
        nights: "Nätter",

        // Steps
        days: "Dagar",
        adults: "Vuxna",
        adultsSub: "Över 12 år",
        children: "Barn",
        childrenSub: "2-11 år",
        findTrip: "Hitta Min Resa"
    },
    en: {
        stepDep: "Where are you traveling from?",
        subDep: "Choose your departure city.",
        stepDest: "Where do you want to go?",
        subDest: "Choose your dream destination.",
        stepDate: "When do you want to go?",
        subDate: "Select travel month.",
        stepDur: "How long?",
        subDur: "Select duration.",
        stepPpl: "Who is traveling?",
        subPpl: "Select travelers.",

        aiInput: "Or describe your dream trip here...",
        aiPlaceholder: "E.g. 'Luxury hotel in Dubai with kids pool near beach...'",
        aiBtn: "Ask AI & Search",
        back: "BACK",

        // Results & Dashboard
        restart: "Start over",
        loading: "Lumi AI is analyzing the best options...",
        insightHeader: "Lumi Insight",
        budgetHeader: "Budget Analysis",
        yourBudget: "Your Budget",
        status: "Status",
        notSpecified: "Not Specified",
        routeHeader: "Route Insight",
        trend: "Trend",
        localTip: "Local Tip",
        recommendations: "Recommendations for you",
        seePrice: "View price",
        select: "Select This",
        hotelPkg: "Hotel & Flight Package",
        uniqueExp: "Unique Experience",
        noResults: "No results found at the moment.",
        tryAgain: "Try again",

        // Vision Board
        vibeCheck: "Vibe Check",
        eternalSummer: "Eternal Summer",
        forecast: "Forecast",
        investment: "Investment",
        highlights: "Highlights",
        visualTimeline: "The Visual Timeline",
        dayLabel: "Day",
        discoverMore: "Discover more",
        expertPicks: "Expert Picks",
        aiInsightLabel: "AI Insight",
        vibe: "Vibe",
        recommendationsLabel: "Recommendations",
        nights: "Nights",

        // Steps
        days: "Days",
        adults: "Adults",
        adultsSub: "12+ years",
        children: "Children",
        childrenSub: "2-11 years",
        findTrip: "Find My Trip"
    }
};

const DEPARTURE_OPTIONS = [
    { id: '10', name: 'Stockholm (Arlanda)', country: 'Sverige', type: 'flight', image: 'https://images.unsplash.com/photo-1509356843151-3e7d96241e11?q=80&w=1200' },
    { id: '6', name: 'Göteborg (Landvetter)', country: 'Sverige', type: 'flight', image: 'https://images.pexels.com/photos/3346227/pexels-photo-3346227.jpeg' },
    { id: '8', name: 'Köpenhamn (Kastrup)', country: 'Danmark', type: 'flight', image: 'https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?q=80&w=1200' },
];

// Fallback static destinations (used when API is not available)
// These are destination-specific high-quality images from Unsplash
const FALLBACK_DESTINATIONS = [
    { id: '55', name: 'Bodrum', vibe: 'Aegean Pearl', image: 'https://images.unsplash.com/photo-1589656966895-2f33e7653819?q=80&w=1200' }, // Bodrum Castle
    { id: '109', name: 'Thailand', vibe: 'Exotic', image: 'https://images.unsplash.com/photo-1528181304800-259b08848526?q=80&w=1200' }, // Thai temples
    { id: '28', name: 'Hurghada', vibe: 'Red Sea', image: 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?q=80&w=1200' }, // Red Sea coral
    { id: '113', name: 'Rhodos', vibe: 'Greek Island', image: 'https://images.unsplash.com/photo-1533105079780-92b9be482077?q=80&w=1200' }, // Rhodes old town
    { id: '71', name: 'Gran Canaria', vibe: 'Canary Islands', image: 'https://images.unsplash.com/photo-1574786198875-c5b3dc1e5691?q=80&w=1200' }, // Canary Islands dunes
    { id: '57', name: 'Dubai', vibe: 'Luxury', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=1200' }, // Dubai skyline
    { id: '76', name: 'Mallorca', vibe: 'Baleares', image: 'https://images.unsplash.com/photo-1568572933382-74d440642117?q=80&w=1200' }, // Mallorca coast
    { id: '6', name: 'Antalya', vibe: 'Mediterranean', image: 'https://images.unsplash.com/photo-1593238739364-18cfde3b2563?q=80&w=1200' }, // Antalya old harbour
];

const TRAVEL_MONTHS = [
    { label: 'Jan', value: '01' }, { label: 'Feb', value: '02' }, { label: 'Mar', value: '03' },
    { label: 'Apr', value: '04' }, { label: 'Maj', value: '05' }, { label: 'Jun', value: '06' },
    { label: 'Jul', value: '07' }, { label: 'Aug', value: '08' }, { label: 'Sep', value: '09' },
    { label: 'Okt', value: '10' }, { label: 'Nov', value: '11' }, { label: 'Dec', value: '12' }
];

const TourAssistPage: React.FC<{ isOpen?: boolean; onClose?: () => void }> = ({ isOpen = true, onClose }) => {
    const { language } = useLocalization();
    const t = (language === 'en' || (language as string) === 'uk') ? TEXTS.en : TEXTS.se; // Fallback to SE
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(VISUAL_STEPS.DEPARTURE);

    // AI Input State
    const [customRequest, setCustomRequest] = useState('');

    const [selections, setSelections] = useState({
        departureId: '',
        destinationId: '',
        month: '',
        duration: 7,
        adults: 2,
        children: 0
    });

    // Dynamic Destinations from API with admin-selected hero images
    const [destinationOptions, setDestinationOptions] = useState<Array<{ id: string, name: string, vibe: string, image: string }>>(FALLBACK_DESTINATIONS);
    const [depImages, setDepImages] = useState<Record<string, string>>({});

    // DB-FIRST: Fetch featured destinations from API (not localStorage)
    useEffect(() => {
        const loadDestinations = async () => {
            try {
                const res = await fetch('/api/destinations?active=true&featured=true');
                const data = await res.json();

                if (data.success && data.destinations && data.destinations.length > 0) {
                    const mappedDestinations = data.destinations.map((d: any) => ({
                        id: d.tourvisioId || d._id,
                        name: d.name || 'Unknown',
                        vibe: d.type?.toString() || 'Paradise',
                        image: d.landingPage?.heroImage || d.heroImage || d.imageUrl || FALLBACK_DESTINATIONS.find(f => f.name.toLowerCase() === (d.name || '').toLowerCase())?.image || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1200'
                    }));

                    if (mappedDestinations.length > 0) {
                        setDestinationOptions(mappedDestinations);
                        console.log('[DB-FIRST] Tour Assist destinations loaded from API:', mappedDestinations.length);
                    }
                }
            } catch (error) {
                console.log('[DB-FIRST] Using fallback destinations:', error);
            }
        };
        loadDestinations();
    }, []);

    // Legacy state for compatibility during refactor if needed, but primarily using new visual flow
    const [plannerData, setPlannerData] = useState<PlannerData | null>(null);

    const [isGenerating, setIsGenerating] = useState(false);
    const [realResults, setRealResults] = useState<Hotel[]>([]);
    const [aiInsight, setAiInsight] = useState<string>('');
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    // Enriched images resolved from HotelCache / local files / Google
    const [enrichedCardImages, setEnrichedCardImages] = useState<Record<string, string>>({});

    // Voice Support State
    const [speechSupported, setSpeechSupported] = useState(false);
    const [isListening, setIsListening] = useState(false);

    useEffect(() => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            setSpeechSupported(true);
        }
    }, []);

    const startListening = () => {
        if (!speechSupported) return;

        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.lang = language === 'en' ? 'en-US' : 'sv-SE';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        setIsListening(true);

        recognition.onresult = (event: any) => {
            const transcript = event.results[0][0].transcript;
            setCustomRequest(transcript);
            setIsListening(false);
            handleTextSearch(transcript);
        };

        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);

        recognition.start();
    };

    const speakText = (text: string) => {
        if (!('speechSynthesis' in window)) return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = language === 'en' ? 'en-US' : 'sv-SE';
        window.speechSynthesis.speak(utterance);
    };

    // Enrich Expert Picks cards with hotel images from HotelCache / local / Google
    useEffect(() => {
        if (!plannerData?.interactive_cards || plannerData.interactive_cards.length === 0) return;

        const enrichImages = async () => {
            const newImages: Record<string, string> = {};
            const promises = plannerData.interactive_cards.map(async (card: any, idx: number) => {
                const hotelName = card.title;
                if (!hotelName) return;

                // Skip if we already have a Tourvisio match
                const matchedHotel = realResults.find(h =>
                    h.name?.toLowerCase().includes(hotelName.toLowerCase()) ||
                    hotelName.toLowerCase().includes(h.name?.toLowerCase().split(' ')[0]) ||
                    (h.name && hotelName && h.name.toLowerCase().split(' ').some(word =>
                        word.length > 3 && hotelName.toLowerCase().includes(word)
                    ))
                );
                if (matchedHotel?.imageUrl) return;

                try {
                    const destName = destinationOptions.find(d => d.id === selections.destinationId)?.name || '';
                    const res = await fetch(`/api/hotel-cache/resolve-image?name=${encodeURIComponent(hotelName)}&city=${encodeURIComponent(destName)}`);
                    const data = await res.json();
                    if (data.success && data.imageUrl) {
                        newImages[hotelName] = data.imageUrl;
                    }
                } catch (e) {
                    // Silently fail — fallback images will be used
                }
            });

            await Promise.allSettled(promises);
            if (Object.keys(newImages).length > 0) {
                setEnrichedCardImages(prev => ({ ...prev, ...newImages }));
            }
        };

        enrichImages();
    }, [plannerData?.interactive_cards, realResults]);

    const handleTextSearch = async (transcript?: string) => {
        const query = (typeof transcript === 'string' ? transcript : customRequest);
        if (!query || !query.trim()) return;

        setIsGenerating(true);
        try {
            const structurePrompt = `
            Act as a high-end luxury travel designer. Convert this user request into a visual VISION BOARD JSON.
            User Request: "${query}"
            
            CRITICAL: Return ONLY raw JSON. No markdown backticks. No explanation.
            Language: ${language === 'en' ? 'English' : 'Swedish'}.
            
            Required JSON Structure (Strict):
            {
                "vibe_title": "Short poetic title (e.g., Aegean Serenity)",
                "assistant_reply": "Inspiring summary (max 25 words)",
                "weather": { "temp": "25°C", "condition": "Sunny", "label": "Perfect Beach Weather" },
                "budget_gauge": { "label": "Mid-Range Luxury", "value": 75, "color": "emerald" },
                "itinerary": [
                    { "day": 1, "title": "Arrival & Sunset", "desc": "Check-in and rooftop dinner" },
                    { "day": 2, "title": "Cultural Dive", "desc": "Museum tour and local lunch" },
                    { "day": 3, "title": "Relaxation", "desc": "Spa morning and beach club" }
                ],
                "places": ["Place 1", "Place 2"],
                "interactive_cards": [
                    { "id": "1", "title": "Hotel Name", "total_price": 5000, "image_url": "https://images.unsplash.com/photo-1540541338287-41700207dee6?q=80&w=600", "badge": "LUMI PICK", "why": "Matches your vibe" }
                ],
                "search_params": { "destination": "city", "duration": 7 }
            }`;

            const aiRes = await sendChatToAI([{ role: 'user', content: structurePrompt } as any], language);
            let text = typeof aiRes === 'string' ? aiRes : (aiRes as any)?.text || "{}";

            // Robust Multilayer Extraction
            let parsed: any = null;
            try {
                let clean = text.trim();
                // 1. Strip markdown if present
                if (clean.includes('```')) {
                    const parts = clean.split(/```(?:json)?/);
                    clean = parts.length > 1 ? parts[1].split('```')[0].trim() : clean;
                }

                // 2. Find first { and last }
                const startIdx = clean.indexOf('{');
                const endIdx = clean.lastIndexOf('}');
                if (startIdx !== -1 && endIdx !== -1) {
                    clean = clean.substring(startIdx, endIdx + 1);
                }

                parsed = JSON.parse(clean);
            } catch (e) {
                console.error("AI Parse Failure, attempting manual recovery", e);
                // Last ditch recovery: if it looks like flat text, put it in assistant_reply
                parsed = { assistant_reply: text.substring(0, 100) };
            }

            if (parsed && (parsed.vibe_title || parsed.assistant_reply)) {
                setPlannerData(parsed);
                setAiInsight(parsed.assistant_reply || "");
                setCurrentStep(VISUAL_STEPS.RESULTS);

                if (transcript || isListening) {
                    speakText(parsed.assistant_reply || "");
                }

                // Also trigger Tourvisio search to get real hotel images
                // Extract destination hint from AI search_params or use default
                const searchDestination = parsed.search_params?.destination || selections.destinationId;
                const now = new Date();
                const checkInDate = new Date(now.getFullYear(), now.getMonth() + 1, 15).toISOString().split('T')[0];

                const tourvisioParams: SearchQuery = {
                    type: SearchType.PackageTours,
                    destinationId: searchDestination || selections.destinationId || '22',
                    checkIn: checkInDate,
                    nights: parsed.search_params?.duration || 7,
                    departureId: selections.departureId || '10',
                    adults: selections.adults || 2,
                    rooms: 1,
                    currency: language === 'sv' ? 'SEK' : 'EUR',
                    nationality: language === 'sv' ? 'SE' : 'DK',
                    children: 0,
                    childrenAges: []
                };

                // Run Tourvisio search in background (non-blocking)
                searchTourvisio(tourvisioParams as any, language as any, (log) => console.log('[Tour Assist AI] Tourvisio log:', log))
                    .then(results => {
                        if (results && Array.isArray(results)) {
                            console.log('[Tour Assist] Tourvisio results for image matching:', results.length);
                            setRealResults(results as Hotel[]);
                        }
                    })
                    .catch(err => console.error('[Tour Assist] Tourvisio search failed:', err));

            } else {
                handleSearch(); // Fallback to normal search
            }

        } catch (e) {
            console.error("AI Error", e);
            handleSearch();
        } finally {
            setIsGenerating(false);
        }
    };



    // Selected Hotel for Detail View
    const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
    const [lastSearchParams, setLastSearchParams] = useState<SearchQuery | null>(null);

    const handleBack = () => {
        if (selectedHotel) {
            setSelectedHotel(null);
            return;
        }
        if (currentStep > 0) setCurrentStep(prev => prev - 1);
    };

    const handleSearch = async () => {
        setCurrentStep(VISUAL_STEPS.RESULTS);
        setIsGenerating(true);
        setRealResults([]);
        setSelectedHotel(null);

        // Calculate check-in date based on selected month (simple heuristic: 15th of next occurrence)
        const now = new Date();
        const selectedMonth = selections.month ? parseInt(selections.month) - 1 : now.getMonth();
        let year = now.getFullYear();
        if (selectedMonth < now.getMonth()) year++; // Next year
        const checkInDate = new Date(year, selectedMonth, 15).toISOString().split('T')[0];

        try {
            // 1. Get AI Insight first for immediate feedback
            const destName = destinationOptions.find(d => d.id === selections.destinationId)?.name || 'Unknown';
            const aiPrompt = `Tell me briefly why ${destName} is great in ${selections.month}/${year} for a ${selections.duration} day trip with ${selections.adults} adults. keep it under 50 words.`;

            // Parallel execution: AI Insight + Tourvisio Search
            const aiPromise = sendChatToAI([{
                id: 'sys', sender: 'user', text: aiPrompt, timestamp: Date.now()
            }], language as any);

            const searchParams: SearchQuery = {
                type: SearchType.PackageTours,
                destinationId: selections.destinationId,
                checkIn: checkInDate,
                nights: selections.duration,
                departureId: selections.departureId,
                adults: selections.adults,
                rooms: 1,
                currency: language === 'sv' ? 'SEK' : 'EUR',
                nationality: language === 'sv' ? 'SE' : 'DK', // Default nationality based on language
                children: selections.children,
                childrenAges: []
            };

            setLastSearchParams(searchParams);

            const searchPromise = searchTourvisio(searchParams as any, language as any, (log) => console.log(log));

            const [aiRes, searchRes] = await Promise.all([aiPromise, searchPromise]);

            // Ensure aiRes is a string or fallback
            const aiText = typeof aiRes === 'string' ? aiRes : (aiRes as any)?.text || "Här är några bra alternativ!";
            setAiInsight(aiText);

            if (searchRes && Array.isArray(searchRes)) {
                setRealResults(searchRes as Hotel[]);
            }

        } catch (error) {
            console.error("Search failed", error);
            setAiInsight("Vi hittade tyvärr inga resultat just nu. Försök med andra datum.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleBooking = (hotel: Hotel) => {
        // Find best offer logic (simplified)
        // Ensure we have a valid offer or construct a robust fallback to avoid crashes
        const offers = (hotel as any).offers || [];
        const bestOffer = offers.length > 0 ? offers[0] : {
            id: 'mock-offer-' + Date.now(),
            price: (hotel as any).totalPrice || hotel.pricePerNight,
            currency: hotel.currency || 'SEK',
            roomType: hotel.roomType || 'Standard Room',
            board: hotel.board || 'Room Only',
            checkIn: lastSearchParams?.checkIn || new Date().toISOString().split('T')[0],
            night: selections.duration
        };

        const searchQuery: SearchQuery = {
            type: SearchType.PackageTours,
            destinationId: selections.destinationId || (hotel.location?.city || ''),
            departureId: selections.departureId || '10', // Default Stockholm if missing
            checkIn: bestOffer.checkIn,
            nights: selections.duration,
            adults: selections.adults,
            children: selections.children,
            rooms: 1,
            nationality: language === 'sv' ? 'SE' : 'DK',
            currency: language === 'sv' ? 'SEK' : 'EUR',
            childrenAges: []
        };

        navigate('/booking', {
            state: {
                hotel: hotel,
                offer: bestOffer,
                searchQuery: searchQuery
            }
        });
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900 relative overflow-x-hidden">
            {/* Minimal Header for visual search context */}
            <div className="absolute top-0 left-0 right-0 p-6 z-50 flex justify-end items-center pointer-events-none">
                {currentStep > 0 && currentStep < VISUAL_STEPS.RESULTS && (
                    <div className="pointer-events-auto">
                        <button onClick={handleBack} className="bg-white/80 backdrop-blur px-6 py-2 rounded-full text-xs font-black uppercase tracking-widest hover:bg-white shadow-sm transition-all">
                            {t.back}
                        </button>
                    </div>
                )}
            </div>


            <div className={`flex flex-col items-center relative px-4 md:px-6 ${currentStep === VISUAL_STEPS.DEPARTURE ? 'min-h-screen pt-8 pb-20 md:py-20 md:justify-center' : currentStep === VISUAL_STEPS.RESULTS ? 'min-h-screen' : 'h-screen justify-center'}`}>

                {/* BACKGROUND DECORATIONS */}
                <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-blue-200/20 rounded-full blur-[100px] pointer-events-none" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-emerald-200/20 rounded-full blur-[100px] pointer-events-none" />

                {/* STEP 0: DEPARTURE */}
                {currentStep === VISUAL_STEPS.DEPARTURE && (
                    <div className="w-full max-w-4xl animate-fade-in-up pb-8 md:pb-0">
                        <h2 className="text-3xl md:text-6xl font-serif text-center mb-4 leading-tight">{t.stepDep}</h2>
                        <p className="text-center text-slate-400 mb-8 md:mb-12 text-base md:text-lg">{t.subDep}</p>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6 mb-8 md:mb-12">
                            {DEPARTURE_OPTIONS.map(dep => (
                                <button
                                    key={dep.id}
                                    onClick={() => {
                                        setSelections({ ...selections, departureId: dep.id });
                                        setCurrentStep(VISUAL_STEPS.DESTINATION);
                                    }}
                                    className="group relative h-[120px] sm:h-[160px] md:h-[200px] rounded-[1.2rem] sm:rounded-[1.5rem] md:rounded-[2rem] overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1 w-full"
                                >
                                    {/* Static High-Quality Image */}
                                    <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url(${dep.image})` }} />

                                    {/* Gradient Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                                    {/* Content */}
                                    <div className="absolute inset-0 flex flex-col items-center justify-center z-10 p-4">
                                        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mb-3 group-hover:bg-blue-600 group-hover:text-white transition-colors text-white border border-white/30">
                                            <Plane size={24} className="md:w-8 md:h-8" />
                                        </div>
                                        <h3 className="text-xl md:text-2xl font-bold text-white shadow-sm">{dep.name.split(' ')[0]}</h3>
                                        <p className="text-[10px] md:text-xs font-bold text-white/70 uppercase tracking-widest">{dep.country}</p>
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* AI Input Section */}
                        <div className="glass-panel p-2 rounded-full flex items-center shadow-xl border border-white/40 max-w-xl mx-auto backdrop-blur-md bg-white/60">
                            {speechSupported && (
                                <button
                                    onClick={startListening}
                                    className={`p-3 rounded-full mr-2 transition-all ${isListening ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-100/50 text-slate-600 hover:bg-slate-200'}`}
                                    title="Speak"
                                >
                                    {isListening ? <Volume2 size={20} /> : <Mic size={20} />}
                                </button>
                            )}
                            <input
                                type="text"
                                value={customRequest}
                                onChange={(e) => setCustomRequest(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleTextSearch()}
                                placeholder={isListening ? (language === 'en' ? "Listening..." : "Lyssnar...") : t.aiPlaceholder}
                                className="flex-1 bg-transparent border-none outline-none px-4 text-slate-800 placeholder:text-slate-500"
                            />
                            <button
                                onClick={() => handleTextSearch()}
                                disabled={isGenerating}
                                className="bg-slate-900 text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-black transition-all flex items-center gap-2"
                            >
                                {isGenerating ? <Loader size={16} className="animate-spin" /> : <Sparkles size={16} />}
                                {t.aiBtn}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 1: DESTINATION */}
                {currentStep === VISUAL_STEPS.DESTINATION && (
                    <div className="w-full max-w-6xl animate-fade-in-up pb-8 md:pb-0">
                        <h2 className="text-3xl md:text-6xl font-serif text-center mb-3 md:mb-4">{t.stepDest}</h2>
                        <p className="text-center text-slate-400 mb-6 md:mb-12 text-base md:text-lg">{t.subDest}</p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 pb-4 md:pb-0">
                            {destinationOptions.map((dest, idx) => (
                                <button
                                    key={dest.id}
                                    onClick={() => {
                                        setSelections({ ...selections, destinationId: dest.id });
                                        setCurrentStep(VISUAL_STEPS.DATE);
                                    }}
                                    className="group relative h-auto aspect-[3/4] sm:aspect-[4/5] rounded-[1.2rem] sm:rounded-[1.5rem] md:rounded-[2rem] overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 bg-slate-200"
                                >
                                    <img src={dest.image} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90 group-hover:opacity-100" />

                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                                    <div className="absolute bottom-6 inset-x-4 text-center">
                                        <p className="text-[9px] font-black text-white/70 uppercase tracking-widest mb-1">{dest.vibe}</p>
                                        <h3 className="text-xl font-bold text-white">{dest.name}</h3>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* STEP 2: DATE */}
                {currentStep === VISUAL_STEPS.DATE && (
                    <div className="w-full max-w-4xl animate-fade-in-up text-center">
                        <h2 className="text-4xl md:text-6xl font-serif mb-4">{t.stepDate}</h2>
                        <p className="text-slate-400 mb-12 text-lg">{t.subDate}</p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {TRAVEL_MONTHS.map(m => (
                                <button
                                    key={m.value}
                                    onClick={() => {
                                        setSelections({ ...selections, month: m.value });
                                        setCurrentStep(VISUAL_STEPS.DURATION);
                                    }}
                                    className="bg-white border-2 border-slate-100 hover:border-blue-600 hover:bg-blue-50 py-8 rounded-2xl text-xl font-bold transition-all shadow-sm hover:shadow-md"
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* STEP 3: DURATION */}
                {currentStep === VISUAL_STEPS.DURATION && (
                    <div className="w-full max-w-4xl animate-fade-in-up text-center">
                        <h2 className="text-4xl md:text-6xl font-serif mb-4">{t.stepDur}</h2>
                        <p className="text-slate-400 mb-12 text-lg">{t.subDur}</p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[5, 7, 10, 14].map(days => (
                                <button
                                    key={days}
                                    onClick={() => {
                                        setSelections({ ...selections, duration: days });
                                        setCurrentStep(VISUAL_STEPS.TRAVELERS);
                                    }}
                                    className="bg-white border-2 border-slate-100 hover:border-blue-600 hover:bg-blue-50 py-12 rounded-[2rem] transition-all shadow-sm hover:shadow-xl group"
                                >
                                    <span className="block text-6xl font-black text-slate-200 group-hover:text-blue-200 mb-2 transition-colors">{days}</span>
                                    <span className="text-lg font-bold text-slate-700">{t.days}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* STEP 4: TRAVELERS */}
                {currentStep === VISUAL_STEPS.TRAVELERS && (
                    <div className="w-full max-w-2xl animate-fade-in-up text-center">
                        <h2 className="text-4xl md:text-6xl font-serif mb-4">{t.stepPpl}</h2>
                        <p className="text-slate-400 mb-12 text-lg">{t.subPpl}</p>


                        <div className="bg-white rounded-[2.5rem] p-12 shadow-xl border border-slate-100 mb-12">
                            <div className="flex items-center justify-between mb-8 pb-8 border-b border-slate-50">
                                <div className="text-left">
                                    <h3 className="text-xl font-bold">{t.adults}</h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{t.adultsSub}</p>
                                </div>
                                <div className="flex items-center gap-6">
                                    <button onClick={() => setSelections({ ...selections, adults: Math.max(1, selections.adults - 1) })} className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center font-black text-lg hover:bg-slate-200 transition-colors">-</button>
                                    <span className="text-3xl font-serif w-8">{selections.adults}</span>
                                    <button onClick={() => setSelections({ ...selections, adults: Math.min(9, selections.adults + 1) })} className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-black text-lg hover:bg-blue-700 transition-colors shadow-lg active:scale-95">+</button>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="text-left">
                                    <h3 className="text-xl font-bold">{t.children}</h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{t.childrenSub}</p>
                                </div>
                                <div className="flex items-center gap-6">
                                    <button onClick={() => setSelections({ ...selections, children: Math.max(0, selections.children - 1) })} className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center font-black text-lg hover:bg-slate-200 transition-colors">-</button>
                                    <span className="text-3xl font-serif w-8">{selections.children}</span>
                                    <button onClick={() => setSelections({ ...selections, children: Math.min(5, selections.children + 1) })} className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-black text-lg hover:bg-blue-700 transition-colors shadow-lg active:scale-95">+</button>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleSearch}
                            className="bg-slate-900 text-white px-12 py-5 rounded-full text-sm font-black uppercase tracking-[0.2em] hover:bg-blue-600 transition-all shadow-2xl hover:scale-105 active:scale-95 w-full md:w-auto"
                        >
                            {t.findTrip}
                        </button>
                    </div>
                )}

                {/* STEP 5: RESULTS & AI */}
                {currentStep === VISUAL_STEPS.RESULTS && !selectedHotel && (
                    <div className="w-full pt-20 pb-10 animate-fade-in">
                        <div className="max-w-7xl mx-auto">
                            <div className="flex flex-col md:flex-row gap-8 items-start mb-12">
                                <div className="flex-1">
                                    <button onClick={() => setCurrentStep(VISUAL_STEPS.DEPARTURE)} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-600 mb-4 transition-colors">
                                        <ArrowLeft size={14} /> {t.restart}
                                    </button>
                                    <h2 className="text-4xl md:text-5xl font-serif text-slate-900 mb-4">
                                        {destinationOptions.find(d => d.id === selections.destinationId)?.name}
                                    </h2>
                                    {isGenerating ? (
                                        <div className="flex flex-col items-center justify-center p-20 text-blue-600 space-y-4">
                                            <div className="relative">
                                                <Sparkles size={48} className="animate-spin-slow" />
                                                <div className="absolute inset-0 animate-ping rounded-full bg-blue-400 opacity-20"></div>
                                            </div>
                                            <span className="font-serif italic text-2xl animate-pulse">{t.loading}</span>
                                        </div>
                                    ) : plannerData ? (
                                        /* VISION BOARD UI */
                                        <div className="w-full space-y-12 animate-fade-in-up">
                                            {/* VIBE HEADER */}
                                            <div className="relative h-[250px] md:h-[400px] rounded-[2rem] md:rounded-[3rem] overflow-hidden group shadow-2xl">
                                                {(realResults.length > 0 && realResults[0]?.imageUrl) || plannerData.interactive_cards?.[0]?.image_url ? (
                                                    <img
                                                        src={realResults[0]?.imageUrl || plannerData.interactive_cards?.[0]?.image_url}
                                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                                                        alt="Vibe"
                                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700" />
                                                )}
                                                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80"></div>
                                                <div className="absolute bottom-6 md:bottom-12 inset-x-6 md:inset-x-12 flex flex-col items-start">
                                                    <span className="bg-white/20 backdrop-blur-md text-white px-4 md:px-6 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.3em] mb-3 md:mb-4 border border-white/30">
                                                        {t.vibeCheck}
                                                    </span>
                                                    <h1 className="text-3xl md:text-5xl lg:text-7xl font-serif text-white mb-2 md:mb-4 drop-shadow-2xl">
                                                        {(plannerData as any).vibe_title || t.eternalSummer}
                                                    </h1>
                                                    <p className="text-base md:text-xl lg:text-2xl text-white/90 max-w-2xl font-medium leading-relaxed italic line-clamp-2 md:line-clamp-none">
                                                        "{plannerData.assistant_reply}"
                                                    </p>
                                                </div>
                                            </div>

                                            {/* BOARD WIDGETS */}
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                                {/* Weather Widget */}
                                                <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-blue-50/50 flex flex-col justify-between">
                                                    <div className="flex justify-between items-start">
                                                        <Cloud size={32} className="text-blue-500" />
                                                        <span className="bg-blue-50 text-blue-600 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-100">
                                                            {t.forecast}
                                                        </span>
                                                    </div>
                                                    <div className="mt-8">
                                                        <p className="text-5xl font-black text-slate-900 mb-2">
                                                            {plannerData.weather?.temp || '24°C'}
                                                        </p>
                                                        <p className="text-lg font-bold text-slate-500">
                                                            {plannerData.weather?.condition || 'Clear Skies'}
                                                        </p>
                                                        <p className="mt-4 text-xs font-black text-blue-600 uppercase tracking-widest bg-blue-50/50 p-2 rounded-lg inline-block">
                                                            {plannerData.weather?.label || 'Ideal for exploration'}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Budget Gauge Widget */}
                                                <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-emerald-50/50">
                                                    <div className="flex justify-between items-start mb-10">
                                                        <Wallet size={32} className="text-emerald-500" />
                                                        <span className="bg-emerald-50 text-emerald-600 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                                                            {t.investment}
                                                        </span>
                                                    </div>
                                                    <div className="relative h-32 flex items-center justify-center">
                                                        {/* Simple Linear Gauge Representation */}
                                                        <div className="w-full">
                                                            <div className="flex justify-between text-xs font-black text-slate-400 mb-2 uppercase tracking-widest">
                                                                <span>Eco</span>
                                                                <span>Premium</span>
                                                                <span>Ultra</span>
                                                            </div>
                                                            <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden border border-slate-200 p-1">
                                                                <div
                                                                    className="h-full bg-emerald-500 rounded-full shadow-lg shadow-emerald-500/30 transition-all duration-1000"
                                                                    style={{ width: `${(plannerData as any).budget_gauge?.value || 60}%` }}
                                                                ></div>
                                                            </div>
                                                            <p className="mt-6 text-center text-xl font-black text-slate-900">
                                                                {(plannerData as any).budget_gauge?.label || 'Premium Selection'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Must-See Spots */}
                                                <div className="bg-slate-900 p-10 rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
                                                    <div className="absolute top-0 right-0 p-8 opacity-20 transform translate-x-4 -translate-y-4 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-700">
                                                        <MapPin size={100} className="text-white" />
                                                    </div>
                                                    <div className="relative z-10">
                                                        <h3 className="text-xs font-black text-white/50 uppercase tracking-[0.3em] mb-8">{t.highlights}</h3>
                                                        <div className="space-y-4">
                                                            {(plannerData.places || ['Bazaar', 'Old Town', 'Marina']).map((p, i) => (
                                                                <div key={i} className="flex items-center gap-4 group/item">
                                                                    <div className="w-2 h-2 rounded-full bg-blue-400 group-hover/item:scale-150 transition-transform"></div>
                                                                    <p className="text-lg font-bold text-white group-hover/item:text-blue-300 transition-colors uppercase tracking-widest">{p}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* IMMERSIVE ITINERARY */}
                                            {plannerData.itinerary && (
                                                <section className="space-y-8">
                                                    <div className="flex items-center gap-4 px-4">
                                                        <Calendar className="text-blue-600" size={24} />
                                                        <h2 className="text-4xl font-serif text-slate-900">{t.visualTimeline}</h2>
                                                    </div>
                                                    <div className="flex overflow-x-auto pb-8 gap-8 px-4 no-scrollbar -mx-4 snap-x">
                                                        {plannerData.itinerary.map((day, i) => (
                                                            <div key={i} className="flex-shrink-0 w-[320px] bg-white p-8 rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-100/50 snap-start hover:border-blue-200 transition-colors group relative overflow-hidden">
                                                                <div className="absolute top-0 right-0 p-4 font-black text-[60px] text-slate-50 group-hover:text-blue-50 transition-colors leading-none pointer-events-none">
                                                                    0{i + 1}
                                                                </div>
                                                                <span className="bg-slate-50 text-slate-400 group-hover:bg-blue-600 group-hover:text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest inline-block mb-6 transition-all">
                                                                    {t.dayLabel} {day.day}
                                                                </span>
                                                                <h4 className="text-2xl font-serif font-bold text-slate-900 mb-4 h-16 line-clamp-2">
                                                                    {day.title}
                                                                </h4>
                                                                <p className="text-slate-500 leading-relaxed font-medium">
                                                                    {day.desc || (day as any).activity}
                                                                </p>
                                                                <div className="mt-8 flex items-center gap-2 text-blue-600 font-black text-[10px] uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    {t.discoverMore} <ArrowRight size={14} />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </section>
                                            )}

                                            {/* TOP RECOMMENDATIONS */}
                                            {plannerData.interactive_cards && (
                                                <section className="space-y-8 pt-8 border-t border-slate-100">
                                                    <div className="flex items-center gap-4 px-4">
                                                        <Sparkles className="text-amber-500" size={24} />
                                                        <h2 className="text-4xl font-serif text-slate-900">{t.expertPicks}</h2>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                        {plannerData.interactive_cards.map((card, idx) => {
                                                            // Match AI card hotel name with Tourvisio realResults
                                                            const matchedHotel = realResults.find(h =>
                                                                h.name?.toLowerCase().includes(card.title?.toLowerCase()) ||
                                                                card.title?.toLowerCase().includes(h.name?.toLowerCase().split(' ')[0]) ||
                                                                (h.name && card.title && h.name.toLowerCase().split(' ').some(word =>
                                                                    word.length > 3 && card.title.toLowerCase().includes(word)
                                                                ))
                                                            );
                                                            const cardImageUrl = matchedHotel?.imageUrl || enrichedCardImages[card.title] || (realResults.length > idx ? realResults[idx]?.imageUrl : null) || card.image_url || '';

                                                            return (
                                                                <div key={idx} className="group flex flex-col lg:flex-row bg-white rounded-[2.5rem] overflow-hidden border border-slate-100 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                                                                    <div className="lg:w-1/3 relative overflow-hidden h-64 lg:h-auto bg-gradient-to-br from-slate-100 to-slate-200">
                                                                        {cardImageUrl ? (
                                                                            <img
                                                                                src={cardImageUrl}
                                                                                alt={card.title}
                                                                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                                                onError={(e) => {
                                                                                    const img = e.currentTarget;
                                                                                    // Cascade: try enriched, then matched, then position-based Tourvisio, then AI's image_url, then hide
                                                                                    const currentSrc = img.src;
                                                                                    const enriched = enrichedCardImages[card.title];
                                                                                    const matched = matchedHotel?.imageUrl;
                                                                                    const fallbackRealResult = realResults.length > idx ? realResults[idx]?.imageUrl : null;
                                                                                    const aiImageUrl = card.image_url;

                                                                                    if (enriched && currentSrc !== enriched) {
                                                                                        img.src = enriched;
                                                                                    } else if (matched && currentSrc !== matched) {
                                                                                        img.src = matched;
                                                                                    } else if (fallbackRealResult && currentSrc !== fallbackRealResult) {
                                                                                        img.src = fallbackRealResult;
                                                                                    } else if (aiImageUrl && currentSrc !== aiImageUrl) {
                                                                                        img.src = aiImageUrl;
                                                                                    } else {
                                                                                        img.style.display = 'none';
                                                                                    }
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                                                                                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2zm16 10L13 12l-6 5m-1-8a2 2 0 100-4 2 2 0 000 4z" /></svg>
                                                                            </div>
                                                                        )}
                                                                        {card.badge && (
                                                                            <span className="absolute top-6 left-6 bg-blue-600 text-white text-[10px] font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-lg transform group-hover:scale-105 transition-transform">
                                                                                {card.badge}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="p-8 lg:w-2/3 flex flex-col justify-between">
                                                                        <div>
                                                                            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-2">
                                                                                <h4 className="text-2xl font-serif font-bold text-slate-900">{card.title}</h4>
                                                                                <span className="text-2xl font-black text-emerald-600 whitespace-nowrap">
                                                                                    {card.total_price > 0 ? `${card.total_price.toLocaleString()} kr` : t.seePrice}
                                                                                </span>
                                                                            </div>
                                                                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
                                                                                <span className="flex items-center gap-1 bg-slate-50 px-3 py-1 rounded-lg">
                                                                                    <Calendar size={14} className="text-blue-500" />
                                                                                    <span className="font-bold uppercase text-[10px] tracking-wider">{card.duration || `7 ${t.nights}`}</span>
                                                                                </span>
                                                                                <span className="flex items-center gap-1 bg-slate-50 px-3 py-1 rounded-lg">
                                                                                    <Sparkles size={14} className="text-amber-500" />
                                                                                    <span className="font-bold uppercase text-[10px] tracking-wider">{card.vibe || 'Luxury'}</span>
                                                                                </span>
                                                                            </div>
                                                                            <p className="text-slate-600 mb-6 line-clamp-2 leading-relaxed font-medium">
                                                                                "{card.why_this_option || card.details_summary}"
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex items-center justify-between mt-4 pt-6 border-t border-slate-50">
                                                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                                                                                {card.hotel_id ? t.hotelPkg : t.uniqueExp}
                                                                            </p>
                                                                            <button className="bg-slate-900 hover:bg-blue-600 text-white px-8 py-3 rounded-full font-bold text-xs uppercase tracking-widest transition-all shadow-lg hover:shadow-blue-500/30 transform active:scale-95">
                                                                                {t.select}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </section>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 max-w-4xl">
                                            <div className="flex items-center gap-2 mb-4 text-blue-600">
                                                <Sparkles size={16} />
                                                <span className="text-[10px] font-black uppercase tracking-widest">{t.aiInsightLabel}</span>
                                            </div>
                                            {(() => {
                                                // Try to parse aiInsight as JSON and render nicely
                                                try {
                                                    let clean = aiInsight.trim();
                                                    if (clean.includes('```')) {
                                                        const parts = clean.split(/```(?:json)?/);
                                                        clean = parts.length > 1 ? parts[1].split('```')[0].trim() : clean;
                                                    }
                                                    const startIdx = clean.indexOf('{');
                                                    const endIdx = clean.lastIndexOf('}');
                                                    if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
                                                        clean = clean.substring(startIdx, endIdx + 1);
                                                        const parsed = JSON.parse(clean);

                                                        // Successfully parsed JSON - render as cards
                                                        return (
                                                            <div className="space-y-6">
                                                                {/* Main Message */}
                                                                {parsed.assistant_reply && (
                                                                    <p className="text-slate-700 text-lg leading-relaxed font-medium italic border-l-4 border-blue-500 pl-4 bg-blue-50/50 py-2 rounded-r-lg">
                                                                        "{parsed.assistant_reply}"
                                                                    </p>
                                                                )}

                                                                {/* Route Insight Cards */}
                                                                {parsed.route_insight && (
                                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                        <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-xl border border-blue-100">
                                                                            <div className="flex items-center gap-2 text-blue-600 mb-2">
                                                                                <Sun size={16} />
                                                                                <span className="text-xs font-black uppercase tracking-wider">{t.vibe}</span>
                                                                            </div>
                                                                            <p className="text-slate-700 font-medium">{parsed.route_insight.vibe}</p>
                                                                        </div>
                                                                        <div className="bg-gradient-to-br from-emerald-50 to-white p-4 rounded-xl border border-emerald-100">
                                                                            <div className="flex items-center gap-2 text-emerald-600 mb-2">
                                                                                <TrendingUp size={16} />
                                                                                <span className="text-xs font-black uppercase tracking-wider">Trend</span>
                                                                            </div>
                                                                            <p className="text-slate-700 font-medium">{parsed.route_insight.trend}</p>
                                                                        </div>
                                                                        <div className="bg-gradient-to-br from-amber-50 to-white p-4 rounded-xl border border-amber-100">
                                                                            <div className="flex items-center gap-2 text-amber-600 mb-2">
                                                                                <Coffee size={16} />
                                                                                <span className="text-xs font-black uppercase tracking-wider">{t.localTip}</span>
                                                                            </div>
                                                                            <p className="text-slate-700 font-medium">{parsed.route_insight.local_tip}</p>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Interactive Cards (Hotels) */}
                                                                {parsed.interactive_cards && parsed.interactive_cards.length > 0 && (
                                                                    <div className="pt-4 border-t border-slate-100">
                                                                        <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
                                                                            <Star size={14} className="text-amber-500" />
                                                                            {t.recommendationsLabel}
                                                                        </h4>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                            {parsed.interactive_cards.slice(0, 4).map((card: any, idx: number) => (
                                                                                <div key={idx} className="flex gap-4 bg-slate-50 p-4 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group border border-slate-100 hover:border-blue-200">
                                                                                    {card.image_url && (
                                                                                        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                                                                                            <img
                                                                                                src={card.image_url}
                                                                                                alt={card.title}
                                                                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <div className="flex items-start justify-between gap-2 mb-1">
                                                                                            <h5 className="font-bold text-slate-800 truncate group-hover:text-blue-600 transition-colors">{card.title}</h5>
                                                                                            {card.badge && (
                                                                                                <span className="text-[8px] font-black bg-blue-600 text-white px-2 py-0.5 rounded uppercase flex-shrink-0">{card.badge}</span>
                                                                                            )}
                                                                                        </div>
                                                                                        {card.duration && <p className="text-xs text-slate-400">{card.duration}</p>}
                                                                                        {(card.why_this_option || card.details_summary) && (
                                                                                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{card.why_this_option || card.details_summary}</p>
                                                                                        )}
                                                                                        {card.total_price > 0 && (
                                                                                            <p className="text-emerald-600 font-bold mt-2">{card.total_price.toLocaleString()} kr</p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Suggestion Chips */}
                                                                {parsed.suggestion_chips && parsed.suggestion_chips.length > 0 && (
                                                                    <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
                                                                        {parsed.suggestion_chips.map((chip: any, idx: number) => (
                                                                            <button key={idx} className="bg-slate-100 hover:bg-blue-100 text-slate-700 hover:text-blue-700 px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1">
                                                                                {chip.icon && <span>{chip.icon}</span>}
                                                                                {chip.label}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                } catch (e) {
                                                    // Parsing failed, show as plain text
                                                }
                                                // Fallback: plain text display
                                                return <p className="text-slate-600 italic leading-relaxed text-sm">"{aiInsight}"</p>;
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* RESULTS GRID */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 mb-20 w-full">
                                {isGenerating ? (
                                    [1, 2, 3, 4].map(i => (
                                        <div key={i} className="h-[400px] bg-white rounded-[2rem] animate-pulse border border-slate-100"></div>
                                    ))
                                ) : realResults.length > 0 ? (
                                    realResults.map(hotel => (
                                        <div key={hotel.id}
                                            onClick={() => setSelectedHotel(hotel)}
                                            className="group relative h-[450px] rounded-[2rem] overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all hover:-translate-y-1 bg-gradient-to-br from-slate-200 to-slate-300"
                                        >
                                            {hotel.imageUrl ? (
                                                <img
                                                    src={hotel.imageUrl}
                                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                    alt={hotel.name}
                                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                                                    <svg className="w-20 h-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"></div>

                                            <div className="absolute top-5 right-5">
                                                <div className="bg-white/10 backdrop-blur-md border border-white/20 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">
                                                    {hotel.rating}★
                                                </div>
                                            </div>

                                            <div className="absolute inset-x-6 bottom-8">
                                                <div className="flex gap-2 mb-2">
                                                    <span className="bg-blue-600/80 text-white text-[8px] font-black px-2 py-0.5 rounded uppercase">{hotel.board}</span>
                                                </div>
                                                <h3 className="text-xl font-bold text-white mb-1 group-hover:text-amber-400 transition-colors truncate">{hotel.name}</h3>
                                                <p className="text-[10px] text-white/50 uppercase tracking-widest mb-4">{hotel.location.city}</p>
                                                <div className="flex justify-between items-center pt-4 border-t border-white/10">
                                                    <span className="text-white text-lg font-black">{(hotel as any).totalPrice || hotel.pricePerNight} {hotel.currency}</span>
                                                    <span className="bg-white text-slate-900 px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest group-hover:bg-blue-600 group-hover:text-white transition-all shadow-lg">
                                                        {t.seePrice}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-4 text-center py-20">
                                        <p className="text-slate-400 mb-4">{t.noResults}</p>
                                        <button onClick={() => setCurrentStep(VISUAL_STEPS.DEPARTURE)} className="text-blue-600 font-bold underline">{t.tryAgain}</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* HOTEL DETAIL OVERLAY */}
                {selectedHotel && (
                    <div className="absolute inset-0 z-40 bg-white animate-fade-in-up overflow-y-auto">
                        <HotelDetail
                            logApiCall={() => { }}
                            embeddedHotel={selectedHotel}
                            embeddedSearchQuery={lastSearchParams!}
                            onClose={() => setSelectedHotel(null)}
                        />
                    </div>
                )}
            </div>
        </div >
    );
};
export default TourAssistPage;
